import BetaBadge from '@cartwhl/ui/atoms/BetaBadge';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import { input } from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';
import Button from '@cartwhl/ui/input/Button';
import PlayerDialog from '@cartwhl/ui/molecules/PlayerDialog';

import { useOpenWithComicPrefill } from '../../AssetLibrary/hooks/useAssetLibraryOpenWithPrefill';
import { useStore, withProvider } from '../batchComicUploadStore';
import type { ComicModel } from '../types';
import ExportOptionsSection from './ExportOptionsSection';
import FileUploadSection from './FileUploadSection';
import RecentComicJobs from './RecentComicJobs';

const COMIC_MAX_PEOPLE = 4;
const NUM_PEOPLE_OPTIONS = Array.from({ length: COMIC_MAX_PEOPLE }, (_, i) => ({
  value: String(i + 1),
  label:
    i > 0 ? (
      <span className="flex items-center gap-2">
        <Text as="span" variant="studio/body/medium/sm">
          {i + 1}
        </Text>
        <BetaBadge label="New" />
      </span>
    ) : (
      String(i + 1)
    ),
}));

const FACIAL_CAPTURE_OPTIONS = [
  { value: 'no', label: 'No' },
  {
    value: 'yes',
    label: (
      <span className="flex items-center gap-2">
        <Text as="span" variant="studio/body/medium/sm">
          Yes
        </Text>
        <BetaBadge label="New" />
      </span>
    ),
  },
];

const COMIC_MODELS: {
  value: ComicModel;
  title: string;
  description: string;
  isNew?: boolean;
}[] = [
  {
    value: 'comic3',
    title: 'Comic 3',
    description: 'Fast, single-actor capture',
  },
  {
    value: 'comic4',
    title: 'Comic 4',
    description: 'Multi-capture with faces up to 4 actors',
    isNew: true,
  },
];

function ComicModelDropdown({
  value,
  onChange,
}: {
  value: ComicModel;
  onChange: (value: ComicModel) => void;
}) {
  const selected =
    COMIC_MODELS.find((model) => model.value === value) ?? COMIC_MODELS[0];

  return (
    <Dropdown
      align="start"
      width={320}
      trigger={
        <button
          type="button"
          className={input({
            className: 'flex h-10 w-full items-center justify-between',
          })}
        >
          <Text variant="studio/body/medium/sm" className="truncate">
            {selected.title} {selected.isNew ? <BetaBadge label="New" /> : null}
          </Text>
          <ChevronDropdownIcon className="text-gray-45 dark:text-gray-60 relative left-1 shrink-0" />
        </button>
      }
    >
      {COMIC_MODELS.map((model) => {
        const isSelected = model.value === value;

        return (
          <Dropdown.Item
            key={model.value}
            icon={isSelected ? <CheckmarkIcon /> : null}
            itemClassName="items-start"
            onClick={() => onChange(model.value)}
          >
            <span className="flex min-w-0 flex-col items-start gap-0.5 text-left">
              <span className="flex items-center gap-2">
                {model.title}
                {model.isNew ? <BetaBadge label="New" /> : null}
              </span>
              <Text
                as="span"
                variant="studio/body/sm"
                className="text-gray-45 font-normal"
              >
                {model.description}
              </Text>
            </span>
          </Dropdown.Item>
        );
      })}
    </Dropdown>
  );
}

function UploadScreen() {
  const store = useStore();
  useOpenWithComicPrefill(store);
  const {
    fileIds,
    libraryVideos,
    uploadModalState,
    uploadedFilesCount,
    totalFilesCount,
  } = store.state;

  const filesCount = fileIds.length + libraryVideos.length;
  const canRunProcess = filesCount > 0;

  // Get the modal message based on state
  const getModalMessage = () => {
    switch (uploadModalState) {
      case 'getting_links':
        return 'Getting upload links...';
      case 'uploading':
        return `Uploading files (${uploadedFilesCount} of ${totalFilesCount})`;
      case 'starting_process':
        return 'Starting process...';
      default:
        return 'Preparing...';
    }
  };

  const isModalVisible = uploadModalState !== 'idle';

  return (
    <div className="flex flex-col">
      <div className="container mx-auto flex flex-col px-4 pt-12 pb-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 shrink-0">
          <Text
            variant="studio/body/md"
            className="text-secondary max-w-[602px]"
          >
            Export your motion in editable 3D formats and seamlessly integrate
            it into any software in your pipeline.
          </Text>
        </div>

        {/* Main content - Two columns */}
        <div className="bg-secondary flex flex-col rounded-2xl p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            {/* Step 1: Add your files */}
            <div className="bg-primary border-primary flex flex-col gap-4 rounded-t-lg border p-6 sm:gap-5 sm:p-8 lg:rounded-none lg:rounded-l-lg lg:pr-6">
              <div className="flex shrink-0 items-center justify-between">
                <Text
                  variant="studio/body/medium/24"
                  className="text-primary"
                  as="h2"
                >
                  <span className="mr-2">1.</span>Add your files
                </Text>
              </div>

              <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-3 sm:items-center sm:gap-y-3">
                <Text variant="studio/body/md" className="order-1 sm:order-0">
                  Model
                </Text>
                <Text variant="studio/body/md" className="order-3 sm:order-0">
                  Actors{' '}
                  <span className="text-gray-60">(max {COMIC_MAX_PEOPLE})</span>
                </Text>
                <Text
                  variant="studio/body/md"
                  className="order-5 text-left sm:order-0"
                >
                  Capture face performances
                </Text>
                <div className="order-2 flex min-w-0 items-center sm:order-0">
                  <ComicModelDropdown
                    value={store.state.comicModel}
                    onChange={(value) => store.updateComicModel(value)}
                  />
                </div>
                <div className="order-4 flex min-w-0 items-center sm:order-0">
                  <Select
                    className="w-full"
                    items={NUM_PEOPLE_OPTIONS}
                    value={String(store.state.numPeople)}
                    onValueChange={(value) =>
                      store.updateNumPeople(parseInt(value, 10))
                    }
                  />
                </div>
                <div className="order-6 flex min-w-0 items-center sm:order-0">
                  <Select
                    className="w-full"
                    items={FACIAL_CAPTURE_OPTIONS}
                    value={store.state.facialCapture ? 'yes' : 'no'}
                    onValueChange={(value) =>
                      store.updateFacialCapture(value === 'yes')
                    }
                  />
                </div>
              </div>

              <div className="border-gray-30 dark:border-gray-60 mt-4 flex flex-col gap-1.5 border-t pt-8">
                <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_17rem] items-center gap-x-3">
                  <Text
                    variant="studio/body/md"
                    className="min-w-0 self-center"
                  >
                    Your video files
                  </Text>
                  <div className="flex h-9 w-full shrink-0 items-center justify-end gap-2">
                    {filesCount > 0 ? (
                      <>
                        <Text
                          variant="studio/body/medium/xs"
                          className="shrink-0 px-3 py-1"
                        >
                          {filesCount} {filesCount === 1 ? 'file' : 'files'}
                        </Text>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="shrink-0"
                          onClick={() => store.clearAll()}
                        >
                          Clear all
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
                <FileUploadSection />
              </div>
            </div>

            {/* Step 2: Set your export options */}
            <div className="bg-primary border-primary flex flex-col gap-5 rounded-b-lg border-r border-b border-l p-6 sm:gap-6 sm:p-8 lg:rounded-none lg:rounded-r-lg lg:border-t lg:border-l-0">
              <Text
                variant="studio/body/medium/24"
                className="text-primary"
                as="h2"
              >
                <span className="mr-2">2.</span>Set your export options
              </Text>
              <div className="flex flex-col gap-2">
                <ExportOptionsSection />
              </div>
            </div>
          </div>

          {/* Run process button */}
          <div className="mt-3 flex shrink-0 justify-end sm:mt-4">
            <Button
              variant="primary"
              size="lg"
              disabled={!canRunProcess}
              onClick={() => store.runProcess()}
            >
              Run process
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto mt-12 px-4 sm:px-6 lg:px-8">
        <div className="border-gray-30 dark:border-gray-75 border-t" />
      </div>
      <RecentComicJobs />

      {/* Upload/Processing Modal */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <PlayerDialog maxWidth={280}>
            <div className="relative flex flex-col items-center pb-2">
              <div className="flex w-full flex-col items-center px-4">
                <Loading className="text-green h-12 w-12" />
                <Text variant="studio/body/sm" className="mt-5 text-center">
                  {getModalMessage()}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-6"
                  onClick={() => store.cancelUpload()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </PlayerDialog>
        </div>
      )}
    </div>
  );
}

export const UploadScreenWithStore = withProvider(UploadScreen);
export default UploadScreen;
